import {
  type CollisionDetection,
  DndContext,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import classNames from 'classnames';
import {
  type CSSProperties,
  type RefObject,
  useCallback,
  useState,
} from 'react';

import { useResizeObserver } from '@revelio/core';

import { useGridLayout } from '../../hooks/useGridLayout';
import { Chart } from '../Chart/ChartRoot/Chart';
import { ChartBuilderPanel } from '../ChartConfig/ChartBuilderPanel';
import { useResolvedItems } from '../DataResolver/DataResolver';
import {
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_BACKGROUND,
} from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { TabContentAction } from '../PageConfigProvider/page-reducer';
import { ReportCoverPage } from '../Reports/ReportCoverPage';
import { ReportFooterPage } from '../Reports/ReportFooterPage';
import ruleStyles from '../Reports/report-page-rule.module.css';
import { ChartClipboardContextMenu } from './components/ChartClipboardContextMenu';
import { GhostPlaceholders } from './components/GhostPlaceholders';
import { Draggable } from './Draggable';
import styles from './EditableGrid.module.css';
import { EditGrid } from './EditGrid';
import { GridActionButton } from './GridActionButton';
import { useGrid } from './GridProvider';
import { useEditableGrid } from './hooks/useEditableGrid';
import { calculateReportGhosts } from './utils/ghost-layout-generator.reports';

const customCollisionDetectionAlgorithm: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return rectIntersection(args);
};

interface ReportEditableGridProps {
  onBuilderOpenChange?: (isOpen: boolean) => void;
  gridContainerRef: RefObject<HTMLDivElement>;
}

export const ReportEditableGrid = ({
  onBuilderOpenChange,
  gridContainerRef,
}: ReportEditableGridProps) => {
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });
  const sensors = useSensors(pointerSensor);

  const { gridItems, grid, edgeDragging, itemDragging } = useGrid();
  const { height: gridContainerHeight } = useResizeObserver(gridContainerRef);
  const {
    primaryFilter,
    dispatch,
    config: tabContentConfig,
  } = useTabContentConfig();
  const { title, description } = tabContentConfig;
  const resolvedItems = useResolvedItems();

  const gridLayout = useGridLayout({
    isReport: true,
    gridRows: grid.rows,
    gridColumns: grid.columns,
  });

  const {
    openConfigId,
    closeConfig,
    selectedIds,
    clearSelection,
    handleChartClick,
    filteredGridItems,
    selectedItem,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
    clipboardContextMenu,
    closeClipboardContextMenu,
    handleClipboardContextMenuAction,
    handleChartContextMenu,
    handleGhostContextMenu,
    handleGhostMouseEnter,
    handleGhostMouseLeave,
  } = useEditableGrid({ onBuilderOpenChange, isReport: true });

  const [dragTransform, setDragTransform] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const onDragEnd = useCallback(
    (event: Parameters<typeof handleDragEnd>[0]) => {
      handleDragEnd(event);
      setDragTransform(null);
    },
    [handleDragEnd]
  );

  const numPages = Math.floor(grid.rows / DEFAULT_GRID_ROWS);
  const footerPageStartY = (numPages - 1) * DEFAULT_GRID_ROWS;

  const ghostPlaceholders = calculateReportGhosts({
    items: gridItems,
    grid: { rows: grid.rows, columns: grid.columns },
  });

  const visibleGhostPlaceholders =
    ghostPlaceholders.length > 0
      ? ghostPlaceholders.filter(
          (ghost) =>
            ghost.position.y >= DEFAULT_GRID_ROWS &&
            ghost.position.y < footerPageStartY
        )
      : ghostPlaceholders;

  const handleAddPage = (pageNumber: number) => {
    dispatch({ type: 'ADD_PAGE', payload: { pageNumber } });
  };
  const handleDeletePageAbove = (pageNumber: number) => {
    dispatch({
      type: 'DELETE_PAGE_ABOVE',
      payload: { pageNumber },
    } as unknown as TabContentAction);
  };
  const isFooterPage = (pageNum: number) => pageNum === numPages;

  const pageDividers = Array.from({ length: numPages }).map((_, index) => {
    const pageNum = index + 1;
    const transformY = (() => {
      if (pageNum === numPages) return 'calc(-100% - 1rem)';
      return '-50%';
    })();

    const pageBreakStyle = {
      '--report-page-break-top': `${pageNum * (gridContainerHeight / numPages)}px`,
    } as CSSProperties;

    if (isFooterPage(pageNum)) {
      return (
        <div
          key={pageNum}
          className={styles.reportPageBreak}
          style={pageBreakStyle}
        >
          <hr className={ruleStyles.rule} />
        </div>
      );
    }

    return (
      <div
        key={pageNum}
        className={styles.reportPageBreak}
        style={pageBreakStyle}
      >
        <hr className={ruleStyles.rule} />
        <GridActionButton
          label="+ Add Page"
          onClick={() => handleAddPage(pageNum)}
          groupProps={{
            position: 'absolute',
            left: '50%',
            transform: `translateX(-50%) translateY(${transformY})`,
            width: '140px',
            backgroundColor: REPORT_PAGE_BACKGROUND,
            zIndex: 1,
          }}
          additionalActions={[
            {
              label: 'Delete Page',
              onClick: () => handleDeletePageAbove(pageNum),
              isDisabled: numPages <= 1,
            },
          ]}
          menuButtonProps={{
            'aria-label': 'More options',
            height: '100%',
            borderRightRadius: 'full',
            backgroundColor: REPORT_PAGE_BACKGROUND,
          }}
          menuPlacement="bottom-end"
        />
      </div>
    );
  });

  return (
    <div className={styles.reportGridRoot}>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        collisionDetection={customCollisionDetectionAlgorithm}
        modifiers={[restrictToParentElement]}
        sensors={sensors}
        autoScroll={false}
      >
        <div
          className={classNames(styles.scrollContainer, styles.reportScroll)}
          style={
            {
              '--report-scroll-overflow': gridLayout.styles.overflow,
            } as CSSProperties
          }
        >
          {(itemDragging || edgeDragging) && (
            <EditGrid
              columns={grid.columns}
              rows={grid.rows}
              width={grid.width}
              height={grid.height}
              paddingLeft={grid.paddingLeft}
              paddingTop={grid.paddingTop}
              trackWidth={grid.trackWidth}
              trackHeight={grid.trackHeight}
              columnGap={grid.columnGap}
              rowGap={grid.rowGap}
            />
          )}
          <div
            id="editable-grid-container"
            ref={gridContainerRef}
            className={classNames(styles.grid, styles.reportEditGrid)}
            style={
              {
                gridTemplateRows: gridLayout.styles.gridTemplateRows,
                gridTemplateColumns: gridLayout.styles.gridTemplateColumns,
                '--report-grid-height': gridLayout.contentHeight,
                '--report-grid-pad-left': gridLayout.styles.paddingLeft,
                '--report-grid-pad-right': gridLayout.styles.paddingRight,
                '--report-edit-page-height': `calc(100% / ${numPages})`,
              } as CSSProperties
            }
          >
            <div className={styles.reportPageLayer}>
              <ReportCoverPage
                title={title ?? ''}
                description={description ?? ''}
                isEditMode={true}
              />
            </div>
            <div
              className={classNames(
                styles.reportPageLayer,
                styles.reportFooterLayer
              )}
            >
              <ReportFooterPage />
            </div>
            <GhostPlaceholders
              ghosts={visibleGhostPlaceholders}
              onAddItem={handleAddItem}
              onContextMenu={handleGhostContextMenu}
              onMouseEnter={handleGhostMouseEnter}
              onMouseLeave={handleGhostMouseLeave}
            />
            {filteredGridItems.map((item) => (
              <Draggable
                id={item.id}
                key={item.id}
                item={item}
                focused={item.id === openConfigId}
                selected={selectedIds.has(item.id)}
                dragTransform={dragTransform}
                setDragTransform={setDragTransform}
                selectedIds={selectedIds}
                onContextMenu={(event) => handleChartContextMenu(item, event)}
                style={{
                  gridColumn: `${item.position.x + 1} / span ${item.position.width}`,
                  gridRow: `${item.position.y + 1} / span ${item.position.height}`,
                }}
              >
                <Chart
                  item={item}
                  primaryFilter={primaryFilter}
                  data={resolvedItems[item.id]}
                  onClick={(e) => handleChartClick(item.id, e)}
                  variant="report"
                />
              </Draggable>
            ))}
            {pageDividers}

            <ChartBuilderPanel
              isOpen={!!openConfigId}
              item={selectedItem}
              selectedIds={selectedIds}
              clearSelection={clearSelection}
              onClose={closeConfig}
            />
          </div>
        </div>
        <ChartClipboardContextMenu
          menu={clipboardContextMenu}
          onAction={handleClipboardContextMenuAction}
          onClose={closeClipboardContextMenu}
        />
      </DndContext>
    </div>
  );
};
